import React, { useState } from 'react';
import { Formik, Field, Form, FormikProps } from 'formik';
import * as Yup from 'yup';
import { Text, Link, TextGroup, TextField, Select, Button, ButtonGroup, Modal } from '@nike/eds';
import styles from './athlete.module.css';
import countryList from '../../../utils/countryList.json';

import ts from './i18n';
import { useI18nContext } from 'context/language/use-i18n-context';
import { useAthleteContext } from 'context/athlete';

const AthleteLogin: React.FC<{ onLoginTypeChange: any; onSuccessfulLogin: any }> = ({
  onLoginTypeChange,
  onSuccessfulLogin,
}) => {
  const [loginError, setLoginError] = useState(false);
  const { i18nString } = useI18nContext();
  const { login } = useAthleteContext();

  const countryOptions = countryList.map((country: any) => ({
    value: country.code,
    label: country.country,
  }));

  const handleSubmit = async (values: any) => {
    try {
      const res = await login({
        country: values.country.value,
        employeeId: values.employeeId,
        password: values.password,
        storeNumber: values.storeNumber,
      });
      if (res) onSuccessfulLogin('/');
    } catch (e) {
      setLoginError(true);
      throw e;
    }
  };

  return (
    <div>
      <TextGroup className="eds-flex eds-flex--justify-content-center eds-flex--direction-column eds-spacing--mt-32">
        <Text
          data-testid="athlete_lgn_hdr_txt"
          className={styles.athlogn_txt}
          font={'title-3'}
          as={'h3'}
        >
          {i18nString(ts.LOGIN_ATHLETE_HEADER)}
        </Text>
        <Link
          data-testid="athlete_lgn_link"
          as="a"
          className={styles.athlogn_txt}
          href="#"
          onClick={() => onLoginTypeChange('okta')}
        >
          {i18nString(ts.LOGIN_NO_ATHLETE_LINK)}
        </Link>
      </TextGroup>
      <Formik
        initialValues={{ employeeId: '', password: '', storeNumber: '', country: '' }}
        validationSchema={Yup.object({
          employeeId: Yup.string().required('Required'),
          password: Yup.string().required('Required'),
          storeNumber: Yup.string().required('Required'),
          country: Yup.object({ value: Yup.string(), label: Yup.string() }).required('Required'),
        })}
        onSubmit={handleSubmit}
      >
        {({ handleChange, values, touched, errors, isValid }: FormikProps<any>) => (
          <Form>
            <TextGroup
              className={`eds-flex eds-flex--justify-content-center eds-flex--direction-column eds-spacing--mt-24 ${styles.athltlgn_txtgrp_width_50}`}
            >
              <TextField
                id="employeeid"
                data-testid="athlete_empid"
                label={i18nString(ts.EMPLOYEE_ID)}
                hideLabel={true}
                type="text"
                name="employeeId"
                onChange={handleChange}
                value={values.employeeId}
                hasErrors={touched.employeeId && !!errors.employeeId}
                placeholder={i18nString(ts.EMPLOYEE_ID)}
                errorMessage={typeof errors.employeeId === 'string' ? errors.employeeId : undefined}
              />
              <TextField
                id="password"
                data-testid="athlete_password"
                label={i18nString(ts.LOGIN_PASSWORD)}
                name="password"
                hideLabel={true}
                type="password"
                onChange={handleChange}
                value={values.password}
                hasErrors={touched.password && !!errors.password}
                placeholder={i18nString(ts.LOGIN_PASSWORD)}
                errorMessage={typeof errors.password === 'string' ? errors.password : undefined}
              />
              <TextField
                data-testid="store_num"
                id="storeNumber"
                label={i18nString(ts.STORE_NUMBER)}
                name="storeNumber"
                hideLabel={true}
                onChange={handleChange}
                type="text"
                value={values.storeNumber}
                hasErrors={touched.storeNumber && !!errors.storeNumber}
                placeholder={i18nString(ts.STORE_NUMBER)}
                errorMessage={
                  typeof errors.storeNumber === 'string' ? errors.storeNumber : undefined
                }
              />
              <Field name="country">
                {({ field }: { field: any }) => (
                  <Select
                    {...field}
                    id="country"
                    data-testid="country"
                    aria-label="country"
                    options={countryOptions}
                    label={i18nString(ts.SELECT_COUNTRY)}
                    name="country"
                    onChange={(option: any) =>
                      handleChange({ target: { name: field.name, value: option } })
                    }
                    placeholder={i18nString(ts.SELECT_COUNTRY)}
                    hideLabel={true}
                    hasErrors={touched.country && !!errors.country}
                    errorMessage={typeof errors.country === 'string' ? errors.country : undefined}
                  />
                )}
              </Field>
              {loginError && (
                <Text className="eds-color--brand-red" font={'body-3'} as={'span'}>
                  {i18nString(ts.INVALID_LOGIN_CREDENTIALS)}
                </Text>
              )}
            </TextGroup>
            <ButtonGroup className="eds-flex eds-flex--justify-content-center eds-spacing--m-24">
              <Button
                data-testid="athlete_lgn_btn"
                disabled={!isValid}
                variant="primary"
                type="submit"
              >
                {i18nString(ts.LOGIN_SUBMIT_FORM)}
              </Button>
            </ButtonGroup>
          </Form>
        )}
      </Formik>
      <Modal
        onDismiss={() => setLoginError(!loginError)}
        isOpen={loginError}
        hideFade
        headerSlot={'Login failed'}
      >
        <div className="eds-flex eds-flex--justify-content-center eds-flex--direction-column">
          <Text font={'body-1'} as={'p'}>
            {i18nString(ts.INVALID_LOGIN_CREDENTIALS)}
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default AthleteLogin;
